import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';

import { useOverviewReport } from '../hooks/use-overview-report';
import { presetRange, todayIn } from '../schemas/report-range';

import { KpiGrid } from './kpi-grid';

const KPIS = ['gross', 'orders', 'aov', 'newCustomers'] as const;

/** Last seven days at a glance on the store home, linking to the full reports. */
export function StoreKpis({ store }: { store: StaffProfileStoresItem }) {
  const { t } = useTranslation('reports');
  const range = presetRange('last7', todayIn(store.timezone));
  const query = useOverviewReport(store.id, range);

  return (
    <section aria-labelledby="store-kpis-title" className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between">
        <h2 id="store-kpis-title" className="text-[15px] font-semibold">
          {t('home.title')}
        </h2>
        <Link
          to="/s/$storeId/reports"
          params={{ storeId: store.id }}
          search={{ range: 'last7' }}
          className="text-primary inline-flex items-center gap-1 text-[13px] hover:underline"
        >
          {t('home.viewReports')}
          <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : (
        <KpiGrid
          kpis={KPIS}
          totals={query.data?.totals}
          previous={query.data?.previous}
          isLoading={query.isPending}
          className="lg:grid-cols-4"
        />
      )}
    </section>
  );
}
